// Instances of a type across scans, as the instance overlay shows them: which object each vertex is,
// where it goes (its frame correction W, then its instance's fit F), the master. Pure functions over
// scans (InstScan): the editor's whole scans (EditSession) and the object page's packs both are one.
import { type Rigid, apply, compose, identity, inverse, isIdentity, transformPoints } from '../align.ts';
import type { V3 } from '../math.ts';
import { BOX_EDGES } from '../volumes.ts';
import { DELETED, MAX_ID, type State, type TypeBox, type TypeVoxels } from './log.ts';
import type { OpSummary } from './session.ts';
import type { PidSet } from './pidset.ts';
import { type Pts, natural } from './instances.ts';

/** What the instance code reads of a scan: its vertices (world P / N, colours, source frames, pids) and edit state. */
export interface InstScan {
  scan: string; nv: number; pidOfV: Uint32Array; P: Float32Array; N: Float32Array; rgb: Uint8Array;
  frame: Uint32Array | null; state: State;
  /** The HEAD path (for which object.of is newest). */
  path(): OpSummary[];
  /** Vertex mask of a saved point group. */
  group(name: string): Uint8Array;
  /** Vertex mask -> the pid set an op carries. */
  toSet(m: Uint8Array): PidSet;
}

/** An object across scans: its type and fit (the newest object.of of the scans wins). */
export type ObjInfo = Map<string, { of?: string; F?: Rigid; t: string }>;

export const idsNamed = (m: Map<number, { name: string }>, name: string) => [...m].filter(([, o]) => o.name === name).map(([id]) => id);

/** Point v of P/N through M (null: as is) into out point j. */
export function place(M: Rigid | null, P: Float32Array, N: Float32Array, v: number, oP: Float32Array, oN: Float32Array, j: number) {
  const i = 3 * v, o = 3 * j, a = P[i], b = P[i + 1], c = P[i + 2], d = N[i], e = N[i + 1], f = N[i + 2];
  if (!M) { oP[o] = a; oP[o + 1] = b; oP[o + 2] = c; oN[o] = d; oN[o + 1] = e; oN[o + 2] = f; return; }
  const R = M.R, t = M.t;
  oP[o] = R[0] * a + R[1] * b + R[2] * c + t[0]; oP[o + 1] = R[3] * a + R[4] * b + R[5] * c + t[1]; oP[o + 2] = R[6] * a + R[7] * b + R[8] * c + t[2];
  oN[o] = R[0] * d + R[1] * e + R[2] * f; oN[o + 1] = R[3] * d + R[4] * e + R[5] * f; oN[o + 2] = R[6] * d + R[7] * e + R[8] * f;
}

/**
 * Tint ids for instances (the shader's hue(fract(id · 0.618)), as for objects), keeping clear of the
 * colours that mean something there: the selection (magenta), the brush (yellow) and deleted (red).
 */
export const INST_IDS = Array.from({ length: 255 }, (_, i) => i + 1).filter(id => {
  const h = (id * 0.618) % 1, near = (c: number, r: number) => Math.min(Math.abs(h - c), 1 - Math.abs(h - c)) < r;
  return !near(5 / 6, 0.1) && !near(1 / 6, 0.07) && !near(0, 0.05);
});
export const instId = (k: number) => INST_IDS[k % INST_IDS.length];
/** The shader's instance tint, hue(fract(id · 0.618)), as rgb 0..1. */
export const hueRgb = (id: number): V3 => {
  const h = (id * 0.618) % 1;
  return [0, 4, 2].map(o => Math.min(1, Math.max(0, Math.abs(((h * 6 + o) % 6) - 3) - 1))) as V3;
};

const live = new WeakMap<State, { ver: number; ids: Set<number> }>();
/** Ids of the objects that have a point (not deleted) in the state; kept until the state changes. */
export function liveObjects(st: State): Set<number> {
  let x = live.get(st);
  if (!x || x.ver !== st.ver) {
    const ids = new Set<number>();
    for (let p = 0; p < st.N; p++) if (st.obj[p] && !(st.flags[p] & DELETED)) ids.add(st.obj[p]);
    live.set(st, (x = { ver: st.ver, ids }));
  }
  return x.ids;
}

/**
 * Objects by name across scans, with their type and fit. A fit is written to the scans loaded at the
 * time, so scans can disagree: the one whose object.of is newest wins. An object with no points left
 * (all released, deleted or moved to another) is gone: it is left out.
 */
export function objectInfo(scans: Iterable<Pick<InstScan, 'state' | 'path'>>): ObjInfo {
  const m: ObjInfo = new Map();
  for (const s of scans) {
    const last = new Map<number, string>();   // object id -> time of its newest object.of on the HEAD path
    for (const o of s.path()) if (o.op === 'object.of' && o.obj !== undefined) last.set(o.obj, o.t);
    const ids = liveObjects(s.state);
    for (const [id, o] of s.state.objects) {
      if (!ids.has(id)) continue;
      const t = last.get(id) ?? '', cur = m.get(o.name);
      if (!cur || t > cur.t) m.set(o.name, { of: o.of, F: o.F, t });
    }
  }
  return m;
}

/** A type's instances by name, in natural order (the first is the reference others are fitted onto). */
export const instancesOf = (info: ObjInfo, type: string) => [...info].filter(([, o]) => o.of === type).map(([n]) => n).sort(natural);

export const typeNames = (info: ObjInfo) => [...new Set([...info.values()].flatMap(o => (o.of ? [o.of] : [])))].sort(natural);

/**
 * Where an object's vertex goes: through its frame's correction W (the object's own, see log.ts Obj),
 * then, with info (placed), the object's fit F into the overlay. null: nowhere else (identity).
 */
export function placer(s: InstScan, info: ObjInfo | null) {
  const st = s.state, fr = s.frame, memo = new Map<number, Rigid | null>();
  return (id: number, v: number): Rigid | null => {
    const f = fr ? fr[v] : 0, key = id * 4294967296 + f;
    let M = memo.get(key);
    if (M === undefined) memo.set(key, (M = motionOf(st, info, id, f)));
    return M;
  };
}

/** Where object `id`'s points of source frame f go: its frame correction W, then (with info) its fit F (null: as they are). */
export function motionOf(st: State, info: ObjInfo | null, id: number, f: number): Rigid | null {
  const o = st.objects.get(id), W = f ? o?.W?.get(f) : undefined, F = info && o ? info.get(o.name)?.F : undefined;
  return F && W ? compose(F, W) : F ?? W ?? null;
}

/** World points of objects (by name) in every scan, deleted points left out, each through its frame corrections and (with info) its fit F. */
export function objectPts(scans: InstScan[], names: string[], info: ObjInfo | null): Map<string, Pts> {
  const want = new Map(names.map((n, i) => [n, i])), count = new Array(names.length).fill(0);
  const per = scans.map(s => {
    const st = s.state, k = new Int32Array(MAX_ID + 1).fill(-1);
    for (const [id, o] of st.objects) { const i = want.get(o.name); if (i !== undefined) k[id] = i; }
    return { s, st, k, pl: placer(s, info) };
  });
  for (const { s, st, k } of per) for (let v = 0; v < s.nv; v++) { const p = s.pidOfV[v], i = k[st.obj[p]]; if (st.obj[p] && i >= 0 && !(st.flags[p] & DELETED)) count[i]++; }
  const out = names.map((_, i) => ({ xyz: new Float32Array(3 * count[i]), nrm: new Float32Array(3 * count[i]), rgb: new Uint8Array(3 * count[i]) }));
  const at = new Array(names.length).fill(0);
  for (const { s, st, k, pl } of per) for (let v = 0; v < s.nv; v++) {
    const p = s.pidOfV[v], i = k[st.obj[p]];
    if (!st.obj[p] || i < 0 || st.flags[p] & DELETED) continue;
    const j = 3 * at[i]++, o = out[i];
    place(pl(st.obj[p], v), s.P, s.N, v, o.xyz, o.nrm, j / 3);
    for (let c = 0; c < 3; c++) o.rgb[j + c] = s.rgb[3 * v + c];
  }
  return new Map(names.map((n, i) => [n, out[i]]));
}

/** An instance's part in one scan: its object id there, point count and world centroid (not fitted). */
export interface InstPart<S extends InstScan = InstScan> { s: S; id: number; n: number; c: V3 }

/**
 * Each instance's part in each scan. Parts of one instance should be one physical object: parts far
 * apart are different objects that ended up with one name.
 */
export function instanceParts<S extends InstScan>(scans: S[], type: string, names: string[]) {
  const out = new Map<string, InstPart<S>[]>();
  for (const s of scans) {
    const st = s.state, acc = new Map<number, number[]>();
    for (const [id, o] of st.objects) if (o.of === type) acc.set(id, [0, 0, 0, 0]);
    if (!acc.size) continue;
    for (let v = 0; v < s.nv; v++) {
      const p = s.pidOfV[v], a = acc.get(st.obj[p]);
      if (!a || st.flags[p] & DELETED) continue;
      a[0] += s.P[3 * v]; a[1] += s.P[3 * v + 1]; a[2] += s.P[3 * v + 2]; a[3]++;
    }
    for (const [id, a] of acc) {
      if (!a[3]) continue;
      const nm = st.objects.get(id)!.name;
      if (!names.includes(nm)) continue;
      (out.get(nm) ?? out.set(nm, []).get(nm)!).push({ s, id, n: a[3], c: [a[0] / a[3], a[1] / a[3], a[2] / a[3]] });
    }
  }
  return out;
}

/** Parts of an instance grouped by place (within `near` m of a group's biggest part), biggest group first. */
export function clusters<P extends { n: number; c: V3 }>(parts: P[], near = 0.6) {
  const groups: P[][] = [];
  for (const p of [...parts].sort((a, b) => b.n - a.n)) {
    const g = groups.find(g => Math.hypot(g[0].c[0] - p.c[0], g[0].c[1] - p.c[1]) < near);
    if (g) g.push(p); else groups.push([p]);
  }
  return groups.sort((a, b) => b.reduce((k, p) => k + p.n, 0) - a.reduce((k, p) => k + p.n, 0));
}

// ── the master: a type's composite template ──────────────────────────────
/** The point group holding a type's master. */
export const masterName = (type: string) => `master ${type}`;

/** Per vertex of a scan, the index (in names) of the instance of type it is in; -1: none, or deleted. */
export function instanceIx(s: InstScan, type: string, names: string[]) {
  const st = s.state, k = new Int32Array(MAX_ID + 1).fill(-1), kv = new Int32Array(s.nv).fill(-1), kOf = new Map(names.map((n, i) => [n, i]));
  for (const [id, o] of st.objects) if (o.of === type && kOf.has(o.name)) k[id] = kOf.get(o.name)!;
  for (let v = 0; v < s.nv; v++) { const p = s.pidOfV[v]; if (st.obj[p] && k[st.obj[p]] >= 0 && !(st.flags[p] & DELETED)) kv[v] = k[st.obj[p]]; }
  return kv;
}

/** Does the type have a master set (a non-empty master group in some scan)? */
export function hasMaster(scans: Iterable<InstScan>, type: string) {
  const g = masterName(type);
  for (const s of scans) if ((s.state.groups.get(g)?.n ?? 0) > 0) return true;
  return false;
}

/** Per vertex: in the type's master? Without a master set, the master is its first instance. */
export function masterMask(s: InstScan, type: string, kv: Int32Array, explicit: boolean) {
  if (explicit) { const g = s.group(masterName(type)); for (let v = 0; v < g.length; v++) g[v] &= kv[v] >= 0 ? 1 : 0; return g; }
  const m = new Uint8Array(kv.length);
  for (let v = 0; v < m.length; v++) m[v] = kv[v] === 0 ? 1 : 0;
  return m;
}

export interface Master { xyz: Float32Array; nrm: Float32Array; inst: Int32Array; explicit: boolean; names: string[] }

/**
 * The master's points, placed in the overlay (each through its frame correction and its instance's fit),
 * with the instance each came from (index in names).
 */
export function masterOf(scans: InstScan[], type: string, names: string[], info: ObjInfo): Master {
  const explicit = hasMaster(scans, type), xyz: number[] = [], nrm: number[] = [], inst: number[] = [], one = new Float32Array(3), on = new Float32Array(3);
  for (const s of scans) {
    const kv = instanceIx(s, type, names), m = masterMask(s, type, kv, explicit), pl = placer(s, info);
    for (let v = 0; v < s.nv; v++) {
      if (!m[v]) continue;
      place(pl(s.state.obj[s.pidOfV[v]], v), s.P, s.N, v, one, on, 0);
      xyz.push(one[0], one[1], one[2]); nrm.push(on[0], on[1], on[2]); inst.push(kv[v]);
    }
  }
  return { xyz: new Float32Array(xyz), nrm: new Float32Array(nrm), inst: Int32Array.from(inst), explicit, names };
}

/** The master's points not from instance `without`, optionally moved by `place` (packed, for target()). */
export function masterPart(m: Master, without: number, move?: Rigid): Pts {
  const keep: number[] = [];
  for (let j = 0; j < m.inst.length; j++) if (m.inst[j] !== without) keep.push(j);
  const xyz = new Float32Array(3 * keep.length), nrm = new Float32Array(3 * keep.length);
  keep.forEach((j, i) => { xyz.set(m.xyz.subarray(3 * j, 3 * j + 3), 3 * i); nrm.set(m.nrm.subarray(3 * j, 3 * j + 3), 3 * i); });
  return move ? { xyz: transformPoints(move, xyz), nrm: transformPoints(move, nrm, undefined, false), rgb: new Uint8Array(0) } : { xyz, nrm, rgb: new Uint8Array(0) };
}

// ── frames: an instance split by where its points came from ──────────────
/** An instance's points from one source frame of one scan: what a frame correction W moves as one. */
export interface FramePiece { k: number; j: number; scan: string; frame: number; id: number; n: number }

/**
 * Overlay points (each its scan index si, vertex v and instance ik) split by instance, scan and source
 * frame (frame 0: a scan without frame ids). The piece of each point, and the pieces in instance,
 * scan, frame order.
 */
export function framePieces(scans: InstScan[], si: Uint16Array, v: Uint32Array, ik: Int32Array) {
  const n = ik.length, raw = new Int32Array(n), found: FramePiece[] = [], at = new Map<number, Map<number, number>>();
  let lastKey = -1, lastF = -1, last = -1;
  for (let i = 0; i < n; i++) {
    const s = scans[si[i]], f = s.frame ? s.frame[v[i]] : 0, key = ik[i] * 65536 + si[i];
    if (key !== lastKey || f !== lastF) {
      const byF = at.get(key) ?? at.set(key, new Map()).get(key)!;
      let x = byF.get(f);
      if (x === undefined) {
        byF.set(f, (x = found.length));
        found.push({ k: ik[i], j: si[i], scan: s.scan, frame: f, id: s.state.obj[s.pidOfV[v[i]]], n: 0 });
      }
      lastKey = key; lastF = f; last = x;
    }
    raw[i] = last;
    found[last].n++;
  }
  const order = found.map((_, x) => x).sort((a, b) => found[a].k - found[b].k || found[a].j - found[b].j || found[a].frame - found[b].frame);
  const re = new Int32Array(found.length);
  order.forEach((x, y) => { re[x] = y; });
  for (let i = 0; i < n; i++) raw[i] = re[raw[i]];
  return { pk: raw, pieces: order.map(x => found[x]) };
}

/** Types' bounding boxes across scans: per type the newest type.box of any scan, with the scans that hold one (a cleared box is left out). */
export function typeBoxes(scans: Iterable<Pick<InstScan, 'scan' | 'state'>>) {
  const out = new Map<string, { box: TypeBox | null; t: string; scans: string[] }>();
  for (const s of scans) for (const [of, x] of s.state.boxes) {
    const cur = out.get(of);
    if (!cur) out.set(of, { box: x.box, t: x.t, scans: [s.scan] });
    else { cur.scans.push(s.scan); if (x.t > cur.t) { cur.box = x.box; cur.t = x.t; } }
  }
  for (const [of, x] of out) if (!x.box) out.delete(of);
  return out as Map<string, { box: TypeBox; t: string; scans: string[] }>;
}

/** Types' voxel models across scans: per type the newest type.voxels of any scan (a cleared model is left out). */
export function typeVoxels(scans: Iterable<Pick<InstScan, 'state'>>) {
  const out = new Map<string, { vox: TypeVoxels | null; t: string }>();
  for (const s of scans) for (const [of, x] of s.state.voxels) { const cur = out.get(of); if (!cur || x.t > cur.t) out.set(of, x); }
  for (const [of, x] of out) if (!x.vox) out.delete(of);
  return out as Map<string, { vox: TypeVoxels; t: string }>;
}

/** Where a voxel model's cells (decodeVoxels: in its reference instance's world) go for instance `name`: F_name⁻¹ · F_ref (as instanceBox). */
export const instanceVoxelM = (vox: TypeVoxels, info: ObjInfo, name: string): Rigid =>
  compose(inverse(info.get(name)?.F ?? identity()), info.get(vox.ref)?.F ?? identity());

/** Where the box sits in the overlay (the fits' frame): F_ref · B. */
export const boxInOverlay = (box: TypeBox, info: ObjInfo): Rigid => compose(info.get(box.ref)?.F ?? identity(), box.B);

/** The box's 8 corners (see BOX_EDGES) placed by M (box frame -> wherever M goes). */
export function boxCorners(M: Rigid, s: V3): V3[] {
  return Array.from({ length: 8 }, (_, i) => apply(M, [(i & 1 ? 0.5 : -0.5) * s[0], (i & 2 ? 0.5 : -0.5) * s[1], (i & 4 ? 0.5 : -0.5) * s[2]]));
}

/** Has the instance been fitted onto the others? The first of its type is what they are fitted onto; another with no fit (or an identity one) is not. */
export function isFitted(info: ObjInfo, name: string) {
  const o = info.get(name);
  return !!o?.of && (instancesOf(info, o.of)[0] === name || !isIdentity(o.F ?? identity()));
}

/** The box around an instance, in the world as its points are (F⁻¹ of where it sits in the overlay); only meaningful for a fitted one (isFitted). */
export const instanceBox = (box: TypeBox, info: ObjInfo, name: string): V3[] =>
  boxCorners(compose(inverse(info.get(name)?.F ?? identity()), boxInOverlay(box, info)), box.s);

/** Line-segment vertices (24 points) of a box's 12 edges, from its corners. */
export function boxEdges(c: V3[]): Float32Array {
  const out = new Float32Array(72);
  BOX_EDGES.forEach(([i, j], k) => { out.set(c[i], 6 * k); out.set(c[j], 6 * k + 3); });
  return out;
}

/**
 * An upright box around points (packed xyz), as box frame -> their frame and edge lengths: turned about z to
 * the points' main horizontal direction (2-D PCA), each side at the lo / 1-lo quantile along its axis (widened by
 * what that leaves out) so a few stray points don't stretch it. null: too few points.
 */
export function fitBox(xyz: Float32Array, lo = 0.01, cap = 200_000): { M: Rigid; s: V3 } | null {
  const n0 = xyz.length / 3, step = Math.max(1, Math.ceil(n0 / cap)), n = Math.ceil(n0 / step);
  if (n0 < 4) return null;
  // the direction from the points inside the quantiles along x, y and z (far strays would swing it)
  const qs = [0, 1, 2].map(k => { const q = new Float32Array(n); for (let i = 0, j = 0; i < n0; i += step, j++) q[j] = xyz[3 * i + k]; q.sort(); const m = Math.min(n - 1, Math.floor(lo * n)); return [q[m], q[n - 1 - m]]; });
  const keep = (i: number) => qs.every(([a, b], k) => xyz[3 * i + k] >= a && xyz[3 * i + k] <= b);
  let mx = 0, my = 0, m = 0;
  for (let i = 0; i < n0; i += step) if (keep(i)) { mx += xyz[3 * i]; my += xyz[3 * i + 1]; m++; }
  mx /= m; my /= m;
  let sxx = 0, sxy = 0, syy = 0;
  for (let i = 0; i < n0; i += step) if (keep(i)) { const x = xyz[3 * i] - mx, y = xyz[3 * i + 1] - my; sxx += x * x; sxy += x * y; syy += y * y; }
  const a = 0.5 * Math.atan2(2 * sxy, sxx - syy), c = Math.cos(a), sn = Math.sin(a);
  const u = new Float32Array(n), v = new Float32Array(n), w = new Float32Array(n);
  for (let i = 0, j = 0; i < n0; i += step, j++) {
    const x = xyz[3 * i], y = xyz[3 * i + 1];
    u[j] = c * x + sn * y; v[j] = -sn * x + c * y; w[j] = xyz[3 * i + 2];
  }
  // the quantiles, widened by the share left out at each end (exact for evenly spread points)
  const span = (q: Float32Array) => {
    q.sort();
    const k = Math.min(n - 1, Math.floor(lo * n)), a = q[k], b = q[n - 1 - k], pad = n > 2 * k ? ((b - a) * k) / (n - 2 * k) : 0;
    return [a - pad, b + pad];
  };
  const [u0, u1] = span(u), [v0, v1] = span(v), [w0, w1] = span(w);
  const cu = (u0 + u1) / 2, cv = (v0 + v1) / 2;
  const M: Rigid = { R: [c, -sn, 0, sn, c, 0, 0, 0, 1], t: [c * cu - sn * cv, sn * cu + c * cv, (w0 + w1) / 2] };
  return { M, s: [Math.max(0.01, u1 - u0), Math.max(0.01, v1 - v0), Math.max(0.01, w1 - w0)] };
}
